// src/components/Navbar.js
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate and useLocation
import logo from '../assets/logo.png'; // Make sure this path is correct

function Navbar({ activeSection }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate(); // Hook for programmatic navigation
  const location = useLocation(); // Hook to get current location object

  // Define navigation items (IDs must match section IDs in HomePage.js)
  const navItems = [
    { id: 'why-us', label: 'Warum?', bgColorVar: 'var(--bg-whyus)' },
    { id: 'details', label: 'Details', bgColorVar: 'var(--bg-details)' },
    { id: 'services', label: 'Angebot', bgColorVar: 'var(--bg-services)' },
    { id: 'portfolio', label: 'Resultate', bgColorVar: 'var(--bg-portfolio)' },
    { id: 'contact', label: 'Kontakt', bgColorVar: 'var(--bg-contact)' },
  ];

  // Function to toggle mobile menu visibility
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Function to handle navigation clicks (for scrolling or changing page)
  const handleNavClick = (sectionId) => {
    // Close mobile menu after any click
    setIsMobileMenuOpen(false);

    // Check if the current location's pathname is the home page
    if (location.pathname === '/' || location.pathname === '') {
      // --- Already on Home Page: Perform smooth scroll ---
      const section = document.getElementById(sectionId);
      if (section) {
        const navbarHeight = 70; // Match your CSS variable --navbar-height
        const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;
        const scrollToPosition = sectionTop - navbarHeight;

        window.scrollTo({
          top: scrollToPosition,
          behavior: 'smooth',
        });
      } else {
        console.warn(`Navbar: Element with ID "${sectionId}" not found for scrolling.`);
      }
    } else {
      // --- Navigating FROM another page (e.g., /rechtliches) TO Home Page section ---
      // Use navigate to go to the home page route ('/') + the hash (#sectionId)
      navigate(`/#${sectionId}`);
      // The scrolling logic for the hash is handled within HomePage.js's useEffect
    }
  };

  // Function to handle clicking the brand/logo link
  const handleBrandClick = () => {
    // Always close the mobile menu if open
    setIsMobileMenuOpen(false);
    // Optionally ensure scrolling to the top when going home
    if (location.pathname === '/' || location.pathname === '') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };


  return (
    // Add class to nav when mobile menu is open for potential styling
    <nav className={`navbar ${isMobileMenuOpen ? 'mobile-menu-active' : ''}`}>
      <div className="container navbar-container">

        {/* --- Brand Link --- */}
        <Link
          to="/"
          className="navbar-brand"
          onClick={handleBrandClick} // Use specific handler
          aria-label="Zur Startseite"
        >
          <img src={logo} alt="JS-Plott-Design Logo" className="navbar-logo" />
          <span className="navbar-company-name">JS-Plott-Design</span>
        </Link>
        {/* --- End Brand Link --- */}


        {/* --- Hamburger Button (Mobile Only) --- */}
        <button
          className={`hamburger-button ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Menü öffnen/schließen"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu-list" // Link button to the menu it controls
        >
           <span className="hamburger-line"></span>
           <span className="hamburger-line"></span>
           <span className="hamburger-line"></span>
        </button>
        {/* --- End Hamburger Button --- */}


        {/* --- Desktop Navigation --- */}
        <ul className="navbar-nav desktop-nav">
          {navItems.map((item) => {
            // Check if the current item's ID matches the active section state
            const isActive = item.id === activeSection;
            return (
              <li key={item.id} className="nav-item">
                <button
                  // Conditionally add 'active' class
                  className={`nav-button ${isActive ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.id)}
                  // Set CSS variable for active color based on section BG
                  style={{ '--active-color': item.bgColorVar }}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
        {/* --- End Desktop Navigation --- */}


        {/* --- Mobile Navigation Menu --- */}
        <div
          id="mobile-menu-list" // ID for aria-controls
          className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
          // Optional: Close menu if clicking the backdrop (only if needed)
          // onClick={toggleMobileMenu}
        >
          {/* Stop propagation if clicking inside the list itself */}
          <ul className="navbar-nav mobile-nav-list" onClick={(e) => e.stopPropagation()}>
            {navItems.map((item) => {
              const isActive = item.id === activeSection;
              return (
                <li key={`mobile-${item.id}`} className="nav-item mobile-nav-item">
                  <button
                    className={`nav-button mobile-nav-button ${isActive ? 'active' : ''}`}
                    onClick={() => handleNavClick(item.id)}
                    style={{ '--active-color': item.bgColorVar }}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
         {/* --- End Mobile Navigation --- */}

      </div>
    </nav>
  );
}

export default Navbar;